import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import stationManagementApi from '../../../../utils/api/location-management';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChartWidthInterface {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Format XAxis tick label
 * @param {string} value The label value
 * @returns {string} The formatted tick label
 */
const formatXAxisLabel = (value: string): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return dayjs(value).format('DD MMM');
  }
  return value;
};

/**
 * Custom square bar component
 * @param {any} properties - The properties
 * @returns {any} The result
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
        />
      ))}
    </>
  );
}

/**
 * Uptime BY Station Chart
 * @returns {any} The rendered component
 */
function UptimeLocationChart() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { selectedLocationPerformanceList } = useAppSelector((state) => state.locationManagement);
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [chartWidth, setChartWidth] = useState<ChartWidthInterface>({
    type: 'percentage',
    width: 100,
  });

  // COLORS — read from theme CSS variables for light/dark support
  const labelColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-label-color')
    .trim();
  const barFillColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-bar-fill-color')
    .trim();

  /**
   * Function to show custom tooltip
   * @param {any} properties - The tooltip props
   * @returns {React.ReactNode} The rendered tooltip
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <div className="tooltip-values">
            <p className="uptime-tooltip-station">
              {t('period', 'Period')}: {data.label}
            </p>
            <p className="uptime-tooltip-hours">
              {t('uptimePercentage', 'Uptime')}: {data.uptimePercent}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch the station's uptime report for a date range. Same day -> hourly
   * buckets (0-23), different days -> one bucket per calendar day.
   * @param {string} startDate The start date string ('YYYY-MM-DD')
   * @param {string} endDate The end date string ('YYYY-MM-DD')
   */
  const fetchPowerConsumedReport = async (startDate: string, endDate: string) => {
    setLoading(true);

    try {
      const response = await stationManagementApi.fetchUptimeChartData({
        startDate,
        endDate,
        chargingStation: selectedLocationPerformanceList?.id,
      });

      if (response.status === 200) {
        const { mode, data } = response.data;

        const formatted = data.map((item: any) => ({
          label: mode === 'hourly' ? `${item.hour}:00` : item.date,
          uptimePercent: item.uptimePercent,
        }));

        setBarGraphData(formatted);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedLocationPerformanceList?.id &&
      selectedLocationPerformanceList.id !== 'all' &&
      dateRange.startDate &&
      dateRange.endDate
    ) {
      fetchPowerConsumedReport(dateRange.startDate, dateRange.endDate);
    }
  }, [selectedLocationPerformanceList?.id, dateRange.startDate, dateRange.endDate]);

  const isRTL = i18n.dir() === 'rtl';

  useEffect(() => {
    if (barGraphData.length <= 7) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 60,
      });
    }
  }, [barGraphData]);

  return (
    <div className="uptime-by-station-chart-wrap" dir={i18n.dir()}>
      <div className="uptime-by-station-container">
        <div className="uptime-by-header">
          <div className="uptime-title">
            <div className="uptime-title-main">
              <span className="title-text">
                {t('stationUptimePercentage', 'Station Uptime Percentage (%)')}
              </span>
              <TooltipIcon text={t('stationuptimepercentagechart')} infoColor="info" />
            </div>
            <p className="subtitle-text">
              {t(
                'dailyUptimePercentageTrendDesc',
                'Uptime percentage trend for the selected station.',
              )}
            </p>
          </div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({
                startDate: start,
                endDate: end,
              });
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
            defaultPreset="last14Days"
          />
        </div>
        <div className="uptime-by-station-chart-container">
          {loading ? (
            <div className="loader-button">
              <BatteryLoader />
            </div>
          ) : (
            <div className="uptime-by-station-chart">
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={220}>
                <BarChart
                  data={barGraphData}
                  margin={{
                    left: isRTL ? 20 : 0,
                    right: isRTL ? 0 : 20,
                    bottom: 10,
                  }}>
                  <XAxis
                    reversed={isRTL}
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{
                      fontSize: 11,
                      fill: labelColor,
                      fontWeight: 500,
                    }}
                    angle={isRTL ? 45 : -45}
                    textAnchor={isRTL ? 'start' : 'end'}
                    dx={isRTL ? 4 : -4}
                    dy={4}
                    height={45}
                    interval={0}
                    tickFormatter={formatXAxisLabel}
                  />
                  <YAxis
                    orientation={i18n.dir() === 'rtl' ? 'right' : 'left'}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: labelColor, fontWeight: 600 }}
                    width={i18n.dir() === 'rtl' ? 65 : 50}
                    tickMargin={i18n.dir() === 'rtl' ? 25 : 0}
                    padding={{ top: 20, bottom: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="uptimePercent"
                    fill={barFillColor}
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UptimeLocationChart;
